<template>
  <modal name="add-fusebox" adaptive height="auto" width="50%" @opened="clearForm" @closed="$emit('clearEdit')">
    <Wrapper>
      <template v-slot:header-title>
        <i class="icon--plus"></i> {{ edit ? $t('fusebox.edit') : $t('fusebox.add_a_new') }}
      </template>
      <ValidationObserver ref="observer" v-slot="{ invalid }" tag="form" class="form__wrapper">
        <div class="field__row">
          <Field :label="$t('fusebox.form.name')"
                 name="name"
                 rules="required|max:255"
                 type="text"
                 v-model="form.name"/>
        </div>
        <div v-if="$can('view', 'labels')" class="field__row">
          <ValidationProvider name="label" v-slot="{ errors }" class="field__item">
            <div :class="{'input__group--red': errors.length > 0}" class="field__item--input field__item--plus flex">
              <span ref="label" class="dynamic-label" :class="{ 'focused' : shouldHaveFocused}">{{ $t('fusebox.form.labels') }}</span>
              <vue-tags-input placeholder=""
                              v-model="tag"
                              :tags="tags"
                              @focus="focused = true" @blur="focused = false"
                              :add-only-from-autocomplete="!$can('view', 'labels')"
                              :autocomplete-items="filteredItems"
                              @tags-changed="newTags => tags = newTags"
              >
              </vue-tags-input>
            </div>
            <span :class="{'form__notice--active': errors.length > 0}"
                  class="form__notice text--red">{{ errors[0] }}</span>
          </ValidationProvider>
        </div>
        <div class="field__flex__dynamic">
          <Field :label="$t('locations.full_address')" style="width: 80%"
                 name="full_address"
                 rules="required|max:255"
                 type="text"
                 v-model="form.full_address">
            <template v-slot:helper-text>
              Eg: Stationshal 12, 3511 CE Utrecht
            </template>
          </Field>
          <button type="button" @click="validate" class="btn">Validate</button>
        </div>
        <div class="field__flex__dynamic">
          <Field :label="$t('locations.no')" style="width: 20%"
                 :readonly="!validated"
                 name="no"
                 type="text"
                 rules="required|max:255"
                 v-model="form.addr_components.no"/>
          <Field :label="$t('locations.address')" style="width: 75%"
                 :readonly="!validated"
                 name="address"
                 type="text"
                 rules="required|max:255"
                 v-model="form.addr_components.address"/>
        </div>
        <div class="field__flex__dynamic">
          <Field :label="$t('locations.city')" style="width: 68%"
                 :readonly="!validated"
                 name="city"
                 type="text"
                 rules="required|max:255"
                 v-model="form.addr_components.city"/>
          <Field :label="$t('locations.zip')" style="width: 30%"
                 :readonly="!validated"
                 name="zip"
                 type="text"
                 rules="required|max:255"
                 v-model="form.addr_components.zip"/>
        </div>
        <div class="field__row">
          <Field :label="$t('locations.contact')"
                 name="contact_name"
                 rules="required|max:255"
                 type="text"
                 v-model="form.contact_name"/>
        </div>
        <div class="field__row">
          <Field :label="$t('locations.mobile')"
                 name="mobile"
                 rules="required|max:255"
                 type="text"
                 v-model="form.mobile"/>
        </div>
        <div class="field__row">
          <FieldSelect :label="$t('locations.network_operator')"
                       name="network_operator"
                       :items="networkList"
                       rules=""
                       :required="false"
                       :placeholder="$t('locations.select_network_operator')"
                       v-model="form.network_operator_id"/>
        </div>
        <div class="field__row">
          <FieldSelect :label="$t('locations.energy_provider')"
                       name="energy_provider"
                       :items="energyList"
                       rules=""
                       :required="false"
                       :placeholder="$t('locations.select_energy_provider')"
                       v-model="form.energy_provider_id"/>
        </div>

        <div class="btn--bar">
          <button v-if="!edit" type="button" @click="save()" :disabled="invalid || submit" class="btn">
            <i class="icon--plus"></i> {{ $t('fusebox.add_new') }}
          </button>
          <button type="button" v-else @click="editForm()" :disabled="invalid || submit" class="btn">
            <i class="icon--check"></i> {{ $t('general.save') }}
          </button>
          <span @click="$emit('hideModal')" class="btn btn--secondary">
            <i class="icon--cancel"></i> {{ $t('general.close') }}
          </span>
        </div>

      </ValidationObserver>
    </Wrapper>
  </modal>
</template>

<script>
import Field from '@/components/Parts/Field.vue'
import { errHandler } from '@/helpers'
import Wrapper from '@/components/Modal/Wrapper'
import FieldSelect from '@/components/Parts/FieldSelect'

export default {
  name: 'NewFuseboxModal',
  props: {
    edit: {
      type: [Boolean, Object],
      default: false
    }
  },
  components: {
    Field,
    Wrapper,
    FieldSelect
  },
  async mounted () {
    await this.init()
  },
  data () {
    return {
      tag: '',
      tags: [],
      locations: [],
      labels: [],
      focused: false,
      validated: false,
      dynWatcher: null,
      form: {
        name: '',
        labels: [],
        contact_name: '',
        full_address: '',
        addr_components: {
          no: '',
          address: '',
          city: '',
          zip: ''
        },
        mobile: null,
        geometry: {
          lat: '',
          lng: ''
        },
        touched: false,
        network_operator_id: null,
        energy_provider_id: null
      },
      submit: false
    }
  },
  computed: {
    shouldHaveFocused () {
      if (this.focused) {
        return true
      }

      return this.tags.length > 0
    },
    filteredItems () {
      return this.labels.filter(i => {
        return i.text.toLowerCase().indexOf(this.tag.toLowerCase()) !== -1
      })
    },
    energyList () {
      return this.$store.getters['location/energyList']
    },
    networkList () {
      return this.$store.getters['location/networkList']
    }
  },
  methods: {
    async init () {
      try {
        const { locations, labels, networks, energies } = await this.$store.dispatch('fusebox/init')
        this.locations = locations
        this.labels = labels
        this.$store.commit('location/setNetworkList', networks)
        this.$store.commit('location/setEnergyList', energies)
      } catch (e) {
      }
    },
    async validate () {
      if (typeof this.dynWatcher === 'function') {
        this.touched = false
        this.dynWatcher()
      }
      try {
        const data = await this.$store.dispatch('geocode', {
          address: this.form.full_address
        })
        this.form.addr_components.no = data.address.street_no
        this.form.addr_components.address = data.address.street_address
        this.form.addr_components.zip = data.address.zip
        this.form.addr_components.city = data.address.city

        this.form.geometry.lat = data.geometry.lat
        this.form.geometry.lng = data.geometry.lng

        this.dynWatcher = this.$watch('form.addr_components', () => {
          this.form.touched = true
        }, { deep: true })
        this.validated = true
      } catch (e) {
        errHandler(e, this.$toasted, key => this.$t(key))
      }
    },
    async save () {
      const isValid = await this.$refs.observer.validate()
      if (isValid) {
        try {
          this.submit = true
          const id = await this.$store.dispatch('fusebox/add', this.form)
          this.$router.push({ name: 'fusebox.single.index', params: { id } })
        } catch (e) {
          errHandler(e, this.$toasted, key => this.$t(key))
        } finally {
          this.submit = false
        }
      }
    },
    async editForm () {
      if (await this.$refs.observer.validate()) {
        try {
          this.submit = true
          await this.$store.dispatch('fusebox/update', {
            uuid: this.edit.uuid,
            form: this.form
          })
          this.$toasted.global.success_message({
            message: this.$t('fusebox.updateSuccess')
          })
          this.$emit('hideModal')
          this.$emit('init')
        } catch (e) {
          errHandler(e, this.$toasted, key => this.$t(key))
        } finally {
          this.submit = false
        }
      }
    },
    clearForm () {
      this.$nextTick(() => {
        if (!this.edit) {
          this.form = {
            name: '',
            labels: [],
            contact_name: '',
            full_address: '',
            addr_components: {
              no: '',
              address: '',
              city: '',
              zip: ''
            },
            mobile: null,
            geometry: {
              lat: '',
              lng: ''
            },
            touched: false,
            network_operator_id: null,
            energy_provider_id: null
          }
          this.tags = []
        } else {
          const tags = this.edit.labels.map(tag => {
            return {
              text: tag
            }
          })
          this.form = {
            name: this.edit.name,
            labels: '',
            contact_name: this.edit.location.name,
            full_address: [this.edit.location.no, this.edit.location.address, this.edit.location.city, this.edit.location.zip].join(' '),
            addr_components: {
              no: this.edit.location.no,
              address: this.edit.location.address,
              city: this.edit.location.city,
              zip: this.edit.location.zip
            },
            mobile: this.edit.location.mobile,
            geometry: {
              lat: this.edit.location.position.coordinates[1],
              lng: this.edit.location.position.coordinates[0]
            },
            touched: false,
            network_operator_id: this.edit.location.network_operator_id,
            energy_provider_id: this.edit.location.energy_provider_id
          }

          this.tags = [...tags]
        }
        requestAnimationFrame(() => {
          this.$refs.observer.reset()
        })
      })
    },
    hide () {
      this.$modal.hide('add-fusebox')
    }
  },
  watch: {
    tags () {
      this.form.labels = this.tags.map(item => {
        return item.text
      })
    }
  }
}
</script>
